import { useMemo } from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';

import { FormTextField } from '../../../../../common';
import { ASSESSMENT_READINESS_ITEM_LABELS } from '../constants';
import {
  ASSESSMENT_READINESS_SCORE_FIELD_SX,
  ASSESSMENT_READINESS_SUBSECTION_HEADER_SX,
  ASSESSMENT_READINESS_SUBSECTION_SX,
  ASSESSMENT_READINESS_TABLE_HEAD_CELL_SX,
  ASSESSMENT_READINESS_TABLE_HEAD_VALUE_CELL_SX,
  ASSESSMENT_READINESS_TABLE_SX,
} from '../styles';
import { formatAssessmentReadinessSectionScore } from '../utils';
import AssessmentReadinessRow from './AssessmentReadinessRow';
import { TREATMENT_PLAN_V3_SECTION_HEADING_SX } from '../../../../styles';

import type { AssessmentReadinessScoreFormValues } from '../types';
import type { AssessmentReadinessSubsectionProps } from './types';

export default function AssessmentReadinessSubsection({
  title,
  items,
  readinessItems,
  sectionScore,
  disabled,
  onUpdateItem,
}: AssessmentReadinessSubsectionProps) {
  const initialValues = useMemo(
    () => ({
      section_score: formatAssessmentReadinessSectionScore(sectionScore, items.length),
    }),
    [items.length, sectionScore],
  );
  const formik = useFormik<AssessmentReadinessScoreFormValues>({
    enableReinitialize: true,
    initialValues,
    onSubmit: () => undefined,
  });

  return (
    <Stack sx={ASSESSMENT_READINESS_SUBSECTION_SX}>
      <Stack sx={ASSESSMENT_READINESS_SUBSECTION_HEADER_SX}>
        <Typography variant="h6" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
          {title}
        </Typography>
        <Box sx={ASSESSMENT_READINESS_SCORE_FIELD_SX}>
          <FormTextField formik={formik} label="Section Score" name="section_score" readOnly />
        </Box>
      </Stack>
      <Table size="small" sx={ASSESSMENT_READINESS_TABLE_SX}>
        <TableHead>
          <TableRow>
            <TableCell sx={ASSESSMENT_READINESS_TABLE_HEAD_CELL_SX}>Item</TableCell>
            <TableCell sx={ASSESSMENT_READINESS_TABLE_HEAD_VALUE_CELL_SX}>Readiness</TableCell>
            <TableCell sx={ASSESSMENT_READINESS_TABLE_HEAD_CELL_SX}>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((itemId) => {
            const savedItem = readinessItems.find((row) => row.item === itemId);
            return (
              <AssessmentReadinessRow
                key={itemId}
                disabled={disabled}
                itemId={itemId}
                label={ASSESSMENT_READINESS_ITEM_LABELS[itemId]}
                notes={savedItem?.notes ?? ''}
                onUpdateItem={onUpdateItem}
                readiness={savedItem?.readiness ?? null}
              />
            );
          })}
        </TableBody>
      </Table>
    </Stack>
  );
}
